import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useResumes } from '../hooks/useResumes'
import { useGoal } from '../hooks/useGoal'
import type { Resume, ResumeContent } from '../types/api'
import { post } from '../lib/api'
import {
  useConfirm,
  useToast,
  StudioLayout,
  ResumePaper,
  Modal,
} from '../components/ui'
import type { ResumePaperContent } from '../components/ui'
import { gettext as _ } from '../lib/i18n'
import styles from './ResumesPage.module.css'

/* ─── types ─── */

const FRAMEWORKS = ['ATS', 'Toss 5줄', 'Amazon LP', 'Google 6축', 'STAR', 'P→S→E→R'] as const

interface ResumeControls {
  packagingLevel: number
  frameworks: string[]
  forbiddenWords: string
  requiredKeywords: string
}

const DEFAULT_CONTROLS: ResumeControls = {
  packagingLevel: 50,
  frameworks: ['STAR'],
  forbiddenWords: '',
  requiredKeywords: '',
}

/* ─── helpers ─── */

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}

/**
 * Adapt the API `ResumeContent` shape to the pure-presentation
 * `ResumePaperContent` shape. The two differ in three places:
 *  - skills: API has `skillGroups: {category, items}[]` (+ flat `skills`),
 *    component wants `{ categories: {name, items}[] }`.
 *  - certifications: API has rich objects, component renders flat strings.
 *  - research: API has {title, description?, period?}, component wants
 *    {title, venue, year}.
 */
function toPaperContent(c: ResumeContent): ResumePaperContent {
  // Handle skills in both formats: flat array or {categories:[...]}
  const rawSkills = c.skills as unknown
  const skillsObj = c.skillGroups
    ? { categories: c.skillGroups.map((g) => ({ name: g.category, items: g.items })) }
    : rawSkills && typeof rawSkills === 'object' && !Array.isArray(rawSkills) && 'categories' in (rawSkills as Record<string, unknown>)
      ? (rawSkills as { categories: Array<{ name: string; items: string[] }> })
      : Array.isArray(rawSkills)
        ? { categories: [{ name: 'Skills', items: rawSkills as string[] }] }
        : undefined

  return {
    summary: c.summary,
    coreCompetencies: c.coreCompetencies,
    experience: c.experience,
    projects: c.projects,
    education: c.education,
    skills: skillsObj,
    certifications: c.certifications?.map((cert) =>
      [cert.name, cert.issuer, cert.period].filter(Boolean).join(' · '),
    ),
    research: c.research?.map((r) => ({
      title: r.title,
      venue: r.description ?? '',
      year: r.period ?? '',
    })),
  }
}

/* ─── small presentational pieces ─── */

function TrackBadge({ track }: { track: string }) {
  const variantClass = (styles as Record<string, string>)[`track_${track}`]
  return (
    <span
      className={`${styles.trackBadge} ${variantClass ?? styles.track_general}`}
    >
      {track}
    </span>
  )
}

/* ─── Left rail: resume selector ─── */

function ResumeSelector({
  resumes,
  selectedId,
  onSelect,
}: {
  resumes: Resume[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  return (
    <div className={styles.selector}>
      <div className={styles.railLabel}>{_('Resumes')}</div>
      {resumes.length === 0 ? (
        <div className={styles.selectorEmpty}>{_('No resumes yet')}</div>
      ) : (
        <ul className={styles.selectorList}>
          {resumes.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className={`${styles.selectorItem} ${
                  selectedId === r.id ? styles.selectorItemActive : ''
                }`}
                onClick={() => onSelect(r.id)}
              >
                <span className={styles.selectorItemTop}>
                  <TrackBadge track={r.track} />
                  <span className={styles.selectorVersion}>v{r.version}</span>
                </span>
                <span className={styles.selectorDate}>
                  {formatDate(r.createdAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ─── Left rail: ATS keyword matching panel ─── */

function AtsPanel({ content }: { content: ResumeContent }) {
  // Normalize skills: could be flat array ["Python"] or object {categories:[...]}
  const flatSkills = Array.isArray(content.skills)
    ? content.skills
    : content.skillGroups
      ? content.skillGroups.flatMap((g) => g.items ?? [])
      : []

  const keywords = Array.from(
    new Set([
      ...(content.coreCompetencies ?? []),
      ...flatSkills,
    ]),
  )
    .filter(Boolean)
    .slice(0, 5)

  return (
    <div className={styles.atsPanel}>
      <div className={styles.atsPanelTitle}>{_('ATS keyword matching')}</div>
      <div className={styles.atsPanelSub}>{_('vs job description')}</div>
      <div className={styles.atsBarGroup}>
        {keywords.length === 0 ? (
          <div className={styles.atsPanelSub}>
            {_('No JD data — upload a JD source for matching')}
          </div>
        ) : (
          keywords.map((kw) => (
            <div key={kw} className={styles.atsBar}>
              <span className={`${styles.atsBarLabel} ${styles.atsBarLabelMet}`}>
                {kw}
              </span>
              <span className={styles.atsBarTrack}>
                <span
                  className={`${styles.atsBarFill} ${styles.atsBarFillGreen}`}
                  style={{ width: '100%' }}
                />
              </span>
              <span className={styles.atsSource}>100%</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

/* ─── Right rail: AI control panel (dashed border) ─── */

function AIControlPanel({
  targetRoles,
  selectedRole,
  onSelectRole,
  generating,
  onGenerate,
  goalLoading,
  talentId,
  sourcesUrl,
  baBefore,
  baAfter,
  onDismissBA,
  notionDisabled,
  onPushNotion,
  pushing,
  onDelete,
  deleting,
  canDelete,
  counselingMode,
  controls,
  onControlsChange,
  onImported,
}: {
  targetRoles: string[]
  selectedRole: string | null
  onSelectRole: (r: string) => void
  generating: boolean
  onGenerate: () => void
  goalLoading: boolean
  talentId: string | undefined
  sourcesUrl: string
  baBefore: string | null
  baAfter: string | null
  onDismissBA: () => void
  notionDisabled: boolean
  onPushNotion: () => void
  pushing: boolean
  onDelete: () => void
  deleting: boolean
  canDelete: boolean
  counselingMode: boolean
  controls: ResumeControls
  onControlsChange: (c: ResumeControls) => void
  onImported: (url: string) => void
}) {
  const [importOpen, setImportOpen] = useState(false)
  const [importUrl, setImportUrl] = useState('')
  const [importing, setImporting] = useState(false)
  const toast = useToast()

  const handleImport = async () => {
    if (!talentId || !importUrl.trim()) return
    if (!importUrl.includes('notion.')) {
      toast.error(_('Invalid URL'))
      return
    }
    setImporting(true)
    try {
      await post(`/api/v1/talents/${talentId}/notion/import-public`, {
        url: importUrl.trim(),
        kind: 'resume',
      })
      toast.success(_('Notion resume imported — AI will counsel-improve it'))
      setImportOpen(false)
      onImported(importUrl.trim())
      setImportUrl('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Import failed'))
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className={styles.aiPanel}>
      <div className={styles.privateHeader}>
        <span className={styles.privateLock}>🔒</span>
        {_('Private panel')}
      </div>

      {/* 1. Notion import — FIRST action */}
      <button
        type="button"
        className={styles.importNotionBtn}
        onClick={() => setImportOpen(true)}
        disabled={!talentId}
      >
        📥 {_('Import from Notion')}
      </button>

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title={_('Import by URL')} size="md">
        <div className={styles.urlImportWrap}>
          <p className={styles.urlImportHint}>{_('Enter public Notion URL')}</p>
          <input
            type="url"
            className={styles.controlInput}
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://workspace.notion.site/..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleImport()
            }}
          />
          <button
            type="button"
            className={styles.importNotionBtn}
            onClick={handleImport}
            disabled={importing || !importUrl.trim()}
            style={{ marginTop: 12 }}
          >
            {importing ? _('Importing...') : _('Import by URL')}
          </button>
        </div>
      </Modal>

      {/* 2. Target role */}
      <div className={styles.aiField}>
        <div className={styles.aiFieldLabel}>{_('Target role')}</div>
        {targetRoles.length > 0 ? (
          <div className={styles.roleChipWrap}>
            {targetRoles.map((role) => (
              <button
                key={role}
                type="button"
                className={`${styles.roleChip} ${
                  selectedRole === role ? styles.roleChipActive : ''
                }`}
                onClick={() => onSelectRole(role)}
              >
                {role}
              </button>
            ))}
          </div>
        ) : goalLoading ? (
          <span className={styles.aiMuted}>{_('Loading...')}</span>
        ) : (
          <Link to={sourcesUrl} className={styles.aiWarnLink}>
            ⚠ {_('Target role not set — set in Sources')}
          </Link>
        )}
      </div>

      {counselingMode && (
        <div className={styles.counselingBadge}>
          {_('Counseling mode — existing resume detected')}
        </div>
      )}

      <div className={styles.controlDivider} />

      {/* 3. Professional controls */}
      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Packaging intensity')}</div>
        <div className={styles.sliderWrap}>
          <div className={styles.sliderValue}>{controls.packagingLevel}</div>
          <input
            type="range"
            min="0"
            max="100"
            value={controls.packagingLevel}
            onChange={(e) =>
              onControlsChange({ ...controls, packagingLevel: Number(e.target.value) })
            }
            className={styles.packSlider}
          />
          <div className={styles.sliderLabels}>
            <span>{_('Fact')}</span>
            <span>{_('Emphasis')}</span>
          </div>
        </div>
        <div
          className={styles.packWarn}
          style={
            controls.packagingLevel >= 90
              ? { color: 'var(--amber)' }
              : undefined
          }
        >
          {controls.packagingLevel >= 90
            ? _('Max emphasis — risk of distortion')
            : controls.packagingLevel >= 70
              ? _('High intensity — review recommended')
              : _('No distortion — highlights strengths')}
        </div>
      </div>

      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Resume framework')}</div>
        <div className={styles.frameworkChips}>
          {FRAMEWORKS.map((fw) => {
            const selected = controls.frameworks.includes(fw)
            return (
              <button
                key={fw}
                type="button"
                className={`${styles.fwChip} ${selected ? styles.fwChipSelected : ''}`}
                onClick={() =>
                  onControlsChange({
                    ...controls,
                    frameworks: selected
                      ? controls.frameworks.filter((f) => f !== fw)
                      : [...controls.frameworks, fw],
                  })
                }
              >
                {selected && <span className={styles.fwCheck}>✓</span>}
                {fw}
              </button>
            )
          })}
        </div>
      </div>

      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Forbidden words')}</div>
        <input
          type="text"
          className={styles.controlInput}
          value={controls.forbiddenWords}
          onChange={(e) =>
            onControlsChange({ ...controls, forbiddenWords: e.target.value })
          }
          placeholder={_('e.g. synergy, passion, disruption')}
        />
      </div>

      <div className={styles.controlField}>
        <div className={styles.controlLabel}>{_('Required keywords')}</div>
        <input
          type="text"
          className={styles.controlInput}
          value={controls.requiredKeywords}
          onChange={(e) =>
            onControlsChange({ ...controls, requiredKeywords: e.target.value })
          }
          placeholder={_('JD keywords to include')}
        />
      </div>

      <div className={styles.controlNote}>
        {_('These settings are private — not exposed in output')}
      </div>

      <div className={styles.controlDivider} />

      {/* 4. Main action — name changes based on counseling mode */}
      <button
        type="button"
        className={styles.magicBtn}
        onClick={onGenerate}
        disabled={generating || !selectedRole}
      >
        {generating ? (
          <>
            <span className={styles.spinner} />{' '}
            {counselingMode ? _('Counseling in progress...') : _('Writing...')}
          </>
        ) : (
          <>
            <span className={styles.sparkle}>✨</span>{' '}
            {counselingMode ? _('AI counseling') : _('Write with AI')}
          </>
        )}
      </button>

      {/* 5. Before → After */}
      {baBefore !== null && baAfter !== null && (
        <div className={styles.baCard}>
          <div className={styles.baLabel}>
            🗣️ {_('AI rewrite — before / after')}
          </div>
          <div className={styles.baBefore}>{baBefore}</div>
          <div className={styles.baArrow}>↓</div>
          <div className={styles.baAfter}>{baAfter}</div>
          <button
            type="button"
            className={styles.baDismiss}
            onClick={onDismissBA}
          >
            {_('Dismiss')}
          </button>
        </div>
      )}

      {/* 6. Export to Notion */}
      <div className={styles.aiDivider} />

      <div className={styles.aiField}>
        <div className={styles.aiFieldLabel}>{_('Export')}</div>
        <button
          type="button"
          className={styles.notionBtn}
          onClick={onPushNotion}
          disabled={notionDisabled || pushing}
        >
          {pushing ? _('Pushing to Notion...') : '📄 ' + _('Write to Notion')}
        </button>
        {talentId && (
          <Link to={`/talents/${talentId}/notion`} className={styles.aiHint}>
            {_('Manage Notion connection')}
          </Link>
        )}
      </div>

      {canDelete && (
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={onDelete}
          disabled={deleting}
        >
          {deleting ? _('Deleting...') : '🗑 ' + _('Delete resume')}
        </button>
      )}
    </div>
  )
}

/* ─── Shared empty-state generate block (used only in the no-resumes state) ─── */

function EmptyGenerateBlock({
  targetRoles,
  selectedRole,
  onSelectRole,
  generating,
  onGenerate,
  sourcesUrl,
  goalLoading,
  talentId,
  onImported,
}: {
  targetRoles: string[]
  selectedRole: string | null
  onSelectRole: (r: string) => void
  generating: boolean
  onGenerate: () => void
  sourcesUrl: string
  goalLoading: boolean
  talentId: string | undefined
  onImported: (url: string) => void
}) {
  const [importOpen, setImportOpen] = useState(false)
  const [importUrl, setImportUrl] = useState('')
  const [importing, setImporting] = useState(false)
  const toast = useToast()

  const handleImport = async () => {
    if (!talentId || !importUrl.trim()) return
    if (!importUrl.includes('notion.')) {
      toast.error(_('Invalid URL'))
      return
    }
    setImporting(true)
    try {
      await post(`/api/v1/talents/${talentId}/notion/import-public`, {
        url: importUrl.trim(),
        kind: 'resume',
      })
      toast.success(_('Notion resume imported — AI will counsel-improve it'))
      setImportOpen(false)
      onImported(importUrl.trim())
      setImportUrl('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Import failed'))
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className={styles.emptyGenerate}>
      <div className={styles.emptyIcon}>📝</div>
      <div className={styles.emptyText}>
        {_('Generate first resume with AI')}
      </div>

      {targetRoles.length > 0 ? (
        <div className={styles.roleChipWrap}>
          {targetRoles.map((role) => (
            <button
              key={role}
              type="button"
              className={`${styles.roleChip} ${
                selectedRole === role ? styles.roleChipActive : ''
              }`}
              onClick={() => onSelectRole(role)}
            >
              {role}
            </button>
          ))}
        </div>
      ) : goalLoading ? (
        <span className={styles.aiMuted}>{_('Loading...')}</span>
      ) : (
        <Link to={sourcesUrl} className={styles.aiWarnLink}>
          ⚠ {_('Target role not set — set in Sources')}
        </Link>
      )}

      <button
        type="button"
        className={styles.magicBtn}
        onClick={onGenerate}
        disabled={generating || !selectedRole}
      >
        {generating ? (
          <>
            <span className={styles.spinner} /> {_('Generating...')}
          </>
        ) : (
          <>
            <span className={styles.sparkle}>✨</span> {selectedRole ? _('AI counseling') : _('Write with AI')}
          </>
        )}
      </button>

      {/* Notion import */}
      <button
        type="button"
        className={styles.importNotionBtn}
        onClick={() => setImportOpen(true)}
        disabled={!talentId}
        style={{ marginTop: 12 }}
      >
        📥 {_('Import from Notion')}
      </button>

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title={_('Import by URL')} size="md">
        <div className={styles.urlImportWrap}>
          <p className={styles.urlImportHint}>{_('Enter public Notion URL')}</p>
          <input
            type="url"
            className={styles.controlInput}
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://workspace.notion.site/..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleImport()
            }}
          />
          <button
            type="button"
            className={styles.importNotionBtn}
            onClick={handleImport}
            disabled={importing || !importUrl.trim()}
            style={{ marginTop: 12 }}
          >
            {importing ? _('Importing...') : _('Import by URL')}
          </button>
        </div>
      </Modal>
    </div>
  )
}

/* ─── Page ─── */

export default function ResumesPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const {
    resumes,
    loading,
    error,
    reload,
    generate,
    generating,
    remove,
  } = useResumes(talentId)
  const { goal, loading: goalLoading } = useGoal(talentId ?? null)
  const toast = useToast()
  const { confirm } = useConfirm()

  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState<string>('summary')
  const [baBefore, setBaBefore] = useState<string | null>(null)
  const [baAfter, setBaAfter] = useState<string | null>(null)
  const [pushing, setPushing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [controls, setControls] = useState<ResumeControls>(DEFAULT_CONTROLS)
  const [liveUrl, setLiveUrl] = useState<string | null>(null)
  const [liveHtml, setLiveHtml] = useState<string | null>(null)
  const [liveError, setLiveError] = useState<string | null>(null)

  // Fetch live HTML from server
  const fetchLiveHtml = useCallback(async (url: string) => {
    if (!talentId) return
    setLiveError(null)
    try {
      const result = await post<{ html: string; title: string }>(
        `/api/v1/talents/${talentId}/notion/render-public`,
        { url },
      )
      setLiveHtml(result.html)
    } catch (e) {
      setLiveHtml(null)
      setLiveError(e instanceof Error ? e.message : '불러오기 실패')
    }
  }, [talentId])

  // Restore live URL from localStorage on mount
  useEffect(() => {
    if (!talentId) return
    try {
      const saved = localStorage.getItem(`cs-live-url-${talentId}`)
      if (saved) {
        setLiveUrl(saved)
        fetchLiveHtml(saved)
      }
    } catch {}
  }, [talentId, fetchLiveHtml])

  const targetRoles = goal?.targetRoles ?? []

  // Sync selectedRole when goal loads.
  useEffect(() => {
    if (targetRoles.length > 0 && !targetRoles.includes(selectedRole ?? '')) {
      setSelectedRole(targetRoles[0] ?? null)
    }
  }, [targetRoles, selectedRole])

  // Keep a valid resume selected (auto-select first/newest).
  useEffect(() => {
    if (resumes.length === 0) {
      if (selectedResumeId !== null) setSelectedResumeId(null)
      return
    }
    const exists = resumes.some((r) => r.id === selectedResumeId)
    if (!exists) {
      const newest = [...resumes].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      )[0]
      setSelectedResumeId(newest?.id ?? null)
    }
  }, [resumes, selectedResumeId])

  const selectedResume = resumes.find((r) => r.id === selectedResumeId) ?? null

  const handleImported = useCallback(async (url: string) => {
    setLiveUrl(url)
    try {
      localStorage.setItem(`cs-live-url-${talentId}`, url)
    } catch {}
    await fetchLiveHtml(url)
  }, [talentId, fetchLiveHtml])

  const handleGenerate = async () => {
    if (!selectedRole) return
    const before = selectedResume?.content?.summary ?? null
    try {
      await generate(selectedRole, {
        packagingLevel: controls.packagingLevel,
        frameworks: controls.frameworks,
        forbiddenWords: controls.forbiddenWords
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        requiredKeywords: controls.requiredKeywords
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      })
      if (before) setBaBefore(before)
      toast.success(_('Resume generated'))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('AI generation failed'))
    }
  }

  // After generation, capture "after" from the newest resume.
  useEffect(() => {
    if (baBefore === null) return
    if (resumes.length === 0) return
    const newest = [...resumes].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    )[0]
    if (
      newest &&
      newest.content?.summary &&
      newest.content.summary !== baBefore
    ) {
      setBaAfter(newest.content.summary)
      setSelectedResumeId(newest.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baBefore, resumes])

  const handlePushNotion = async () => {
    if (!selectedResume || !talentId) return
    setPushing(true)
    try {
      const res = await post<{ notionPageId: string; notionUrl: string }>(
        `/api/v1/talents/${talentId}/notion/push`,
        {
          type: 'resume',
          id: selectedResume.id,
          title: `${selectedResume.track} 이력서`,
        },
      )
      toast.success(_('Pushed to Notion: {url}', { url: res.notionUrl }))
      await reload()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Notion push failed'))
    } finally {
      setPushing(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedResume) return
    const ok = await confirm({
      message: _('Delete this resume?'),
      danger: true,
    })
    if (!ok) return
    setDeleting(true)
    try {
      await remove(selectedResume.id)
      toast.success(_('Resume deleted'))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : _('Delete failed'))
    } finally {
      setDeleting(false)
    }
  }

  const sourcesUrl = `/talents/${talentId ?? ''}/sources`

  /* ─── Loading state ─── */
  if (loading) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.stateWrap}>
          <div className={styles.loadingWrap}>
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  /* ─── Error state ─── */
  if (error) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.stateWrap}>
          <div className={styles.errorWrap}>
            <span className={styles.errorMsg}>{error}</span>
            <button className={styles.retryBtn} onClick={reload}>
              {_('Retry')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  /* ─── Empty state ━━━ no resumes AND no live URL ━━━ */
  if (resumes.length === 0 && !liveUrl) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.stateWrap}>
          <div className={styles.emptyWrap}>
            <EmptyGenerateBlock
              targetRoles={targetRoles}
              selectedRole={selectedRole}
              onSelectRole={setSelectedRole}
              generating={generating}
              onGenerate={handleGenerate}
              sourcesUrl={sourcesUrl}
              goalLoading={goalLoading}
              talentId={talentId}
              onImported={handleImported}
            />
          </div>
        </div>
      </div>
    )
  }

  /* ─── Studio workspace (3-column) ─── */
  return (
    <div className={styles.pageRoot}>
      <div className={styles.studioWrap}>
        <StudioLayout
          left={
            <>
              <ResumeSelector
                resumes={resumes}
                selectedId={selectedResumeId}
                onSelect={setSelectedResumeId}
              />
              {selectedResume && (
                <>
                  <div className={styles.sectionNav}>
                    <div className={styles.atsPanelTitle}>{_('Sections')}</div>
                    {[
                      { key: 'summary', label: _('Summary'), color: 'var(--indigo)' },
                      { key: 'experience', label: _('Experience'), color: 'var(--cyan)' },
                      { key: 'projects', label: _('Projects'), color: 'var(--violet)' },
                      { key: 'skills', label: _('Skills'), color: 'var(--green)' },
                      { key: 'education', label: _('Education'), color: 'var(--amber)' },
                    ].map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        className={`${styles.sectionNavItem} ${
                          activeSection === s.key ? styles.sectionNavItemActive : ''
                        }`}
                        onClick={() => {
                          setActiveSection(s.key)
                          if (!liveUrl) {
                            document
                              .getElementById(`section-${s.key}`)
                              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                        }}
                      >
                        <span
                          className={styles.sectionDot}
                          style={{ background: s.color }}
                        />
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <AtsPanel content={selectedResume.content} />
                </>
              )}
            </>
          }
          right={
            <AIControlPanel
              targetRoles={targetRoles}
              selectedRole={selectedRole}
              onSelectRole={setSelectedRole}
              generating={generating}
              onGenerate={handleGenerate}
              goalLoading={goalLoading}
              talentId={talentId}
              sourcesUrl={sourcesUrl}
              baBefore={baBefore}
              baAfter={baAfter}
              onDismissBA={() => {
                setBaBefore(null)
                setBaAfter(null)
              }}
              notionDisabled={!selectedResume}
              onPushNotion={handlePushNotion}
              pushing={pushing}
              onDelete={handleDelete}
              deleting={deleting}
              canDelete={!!selectedResume}
              counselingMode={!!selectedResume?.counselingMode}
              controls={controls}
              onControlsChange={setControls}
              onImported={handleImported}
            />
          }
        >
          {selectedResume || liveUrl ? (
            <div className={styles.centerScroll}>
              {/* Status bar */}
              {selectedResume && (
                <div className={styles.statusBar}>
                  {selectedResume.atsScore != null && (
                    <span className={`${styles.statusBadge} ${styles.statusBadgeCyan}`}>
                      <span className={styles.statusCheck}>✓</span>
                      ATS {selectedResume.atsScore}%
                    </span>
                  )}
                  {controls.frameworks.map((fw) => (
                    <span key={fw} className={`${styles.statusBadge} ${styles.statusBadgeViolet}`}>
                      <span className={styles.statusCheck}>✓</span>
                      {fw}
                    </span>
                  ))}
                  {selectedResume.counselingMode && (
                    <span className={`${styles.statusBadge} ${styles.statusBadgeGreen}`}>
                      <span className={styles.statusCheck}>✓</span>
                      {_('Counseling')}
                    </span>
                  )}
                </div>
              )}
              {liveUrl && (
                <div className={styles.statusBar}>
                  <span className={`${styles.statusBadge} ${styles.statusBadgeCyan}`}>
                    ● {_('Live Notion page')}
                  </span>
                </div>
              )}

              {/* Before → After card */}
              {baBefore !== null && baAfter !== null && (
                <div className={styles.baCardCenter}>
                  <div className={styles.baLabel}>
                    🗣️ {_('AI rewrite — before / after')}
                  </div>
                  <div className={styles.baBefore}>{baBefore}</div>
                  <div className={styles.baArrow}>↓</div>
                  <div className={styles.baAfter}>{baAfter}</div>
                </div>
              )}

              {/* Center: Live Notion page (direct iframe) OR AI-generated paper */}
              {liveUrl ? (
                <div className={styles.liveFrameWrap}>
                  {liveHtml ? (
                    <iframe
                      srcDoc={liveHtml}
                      className={styles.liveFrame}
                      sandbox="allow-same-origin"
                      title="Notion Live"
                    />
                  ) : liveError ? (
                    <div className={styles.centerEmpty}>
                      <div>⚠ {liveError}</div>
                      <button
                        type="button"
                        className={styles.retryBtn}
                        onClick={() => liveUrl && fetchLiveHtml(liveUrl)}
                      >
                        {_('Retry')}
                      </button>
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => {
                          setLiveUrl(null)
                          setLiveHtml(null)
                          try { localStorage.removeItem(`cs-live-url-${talentId}`) } catch {}
                        }}
                      >
                        {_('Dismiss')}
                      </button>
                    </div>
                  ) : (
                    <div className={styles.centerEmpty}>
                      <div className={styles.spinner} />
                      <div>{_('Loading Notion...')}</div>
                    </div>
                  )}
                </div>
              ) : selectedResume ? (
                <ResumePaper content={toPaperContent(selectedResume.content)} />
              ) : null}
            </div>
          ) : (
            <div className={styles.centerEmpty}>
              <div className={styles.emptyText}>{_('Select a resume')}</div>
            </div>
          )}
        </StudioLayout>
      </div>
    </div>
  )
}
